import React, { createContext, useState, useContext, ReactNode, useCallback, useRef } from 'react';

type UserMenuContextType = {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  openSidebar: () => void;
  closeSidebar: () => void;
  closeWithDelay: (delay?: number) => void;
  cancelCloseDelay: () => void;
};

export const UserMenuContext = createContext<UserMenuContextType | undefined>(undefined);

export const UserMenuProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearTimer = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const toggleSidebar = useCallback(() => {
    clearTimer();
    setSidebarOpen(prev => !prev);
  }, [clearTimer]);

  const openSidebar = useCallback(() => {
    clearTimer();
    setSidebarOpen(true);
    window.dispatchEvent(new CustomEvent('sidebar-opened', { detail: { type: 'usermenu' } }));
  }, [clearTimer]);

  const closeSidebar = useCallback(() => {
    clearTimer();
    setSidebarOpen(false);
  }, [clearTimer]);

  const closeWithDelay = useCallback((delay: number = 1000) => {
    clearTimer();
    closeTimeoutRef.current = setTimeout(() => {
      setSidebarOpen(false);
      closeTimeoutRef.current = null;
    }, delay);
  }, [clearTimer]);

  const cancelCloseDelay = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  // Clean up on unmount
  React.useEffect(() => {
    return () => clearTimer();
  }, [clearTimer]);

  // Listen for other sidebars opening
  React.useEffect(() => {
    const handleSidebarOpened = (event: CustomEvent) => {
      if (event.detail.type !== 'usermenu') {
        clearTimer();
        setSidebarOpen(false);
      }
    };
    window.addEventListener('sidebar-opened', handleSidebarOpened as EventListener);
    return () => {
      window.removeEventListener('sidebar-opened', handleSidebarOpened as EventListener);
    };
  }, [clearTimer]);

  React.useEffect(() => {
    const handleOpenUserMenu = () => {
      clearTimer();
      setSidebarOpen(true);
      window.dispatchEvent(new CustomEvent('sidebar-opened', { detail: { type: 'usermenu' } }));
    };

    window.addEventListener('open-user-menu', handleOpenUserMenu as EventListener);
    return () => {
      window.removeEventListener('open-user-menu', handleOpenUserMenu as EventListener);
    };
  }, [clearTimer]);

  return (
    <UserMenuContext.Provider value={{ sidebarOpen, toggleSidebar, openSidebar, closeSidebar, closeWithDelay, cancelCloseDelay }}>
      {children}
    </UserMenuContext.Provider>
  );
};

export function useUserMenu() {
  const context = useContext(UserMenuContext);
  if (context === undefined) {
    throw new Error('useUserMenu must be used within a UserMenuProvider');
  }
  return context;
}
